import axios from 'axios';
import { FIGMA_API_BASE_URL } from '@/services/figma';
import { getFigmaToken } from '@/services/figma-token.service';
import type { ExternalFigmaNode } from '@/components/Nodes/FigmaCompositor/utils';
import type { ImageFile } from '@/designer/designer';

interface NodePreviewResponse {
  images: Record<string, string>;
}

const figmaAxios = axios.create({
  baseURL: FIGMA_API_BASE_URL,
});

figmaAxios.interceptors.request.use(async (config) => {
  const token = await getFigmaToken();
  if (!token) throw new Error('No Figma access token available. Please connect to Figma first.');
  config.headers.Authorization = `Bearer ${token}`;
  return config;
});

interface WeavyConnections {
  input: WeavyInputConnection | null;
  compositor: WeavyCompositorConnection | null;
}

interface WeavyInputConnection {
  weavyInputNodeId: string;
  paintId?: string;
}

interface WeavyCompositorConnection {
  weavyRecipeId: string;
  compositorNodeId: string;
  id: string;
  width: number;
  height: number;
  inputConnections: Array<{
    figmaNodeId: string;
    weavyInputNodeId: string;
    paintId?: string;
  }>;
  thumbnail: WeavyCompositorThumbnail;
}

export interface WeavyCompositorThumbnail {
  url: string;
  width: number;
  height: number;
}

interface NodeRequestBase {
  name?: string;
  width?: number;
  height?: number;
  text?: {
    content: string;
  };
}

// --- Connect node ---

interface ConnectNodeRequest {
  weavyRecipeId: string;
  compositorNodeId: string;
  force?: boolean;
}
interface ConnectNodeResponse {
  id: string;
  type: string;
  name: string;
  thumbnail: { url: string; width: number; height: number };
}

// --- Create node ---

interface CreateNodeRequest extends NodeRequestBase {
  parent_id: string;
  node_type: 'FRAME' | 'TEXT';
  name: string;
  weavy_input_node_id: string;
  fills?: ImageFill[];
}

interface ImageFill {
  url: string;
  type: 'IMAGE';
  blendMode: 'NORMAL';
  scaleMode: 'FILL';
}

interface CreateWeavyConnectedNodeResponse {
  id: string;
  type: string;
  name: string;
  fills: Array<{ id: string; type: string }>;
  weavyConnections?: WeavyConnections;
}

// --- Update node ---
interface UpdateNodeRequest {
  fill?: UpdateNodeFillPayload;
  text?: { content: string };
  weavy_compositor_connection?: { weavy_recipe_id: string; compositor_node_id: string; force?: boolean } | null;
  weavy_input_connection?: { parent_node_guid: string; weavy_input_node_id: string } | null;
}

type UpdateNodeFillPayload =
  | {
      action: 'CREATE';
      type: 'IMAGE';
      url: string;
      blendMode?: string;
      scaleMode?: string;
    }
  | {
      action: 'UPDATE';
      type: 'IMAGE';
      paint_id: string;
      url?: string;
      blendMode?: string;
      scaleMode?: string;
    }
  | {
      action: 'DELETE';
      type: 'IMAGE';
      paint_id: string;
    };

// --- Get nodes ---
type GetNodesResponse = ExternalFigmaNode;

export class FigmaApiClient {
  async fetchFigmaNodePreview(fileKey: string, nodeId: string): Promise<ImageFile | undefined> {
    const { data } = await figmaAxios.get<NodePreviewResponse>(
      `/v1/images/${fileKey}?ids=${nodeId}&format=png&scale=1`,
    );

    const imageUrl = data.images[nodeId];
    if (!imageUrl) return undefined;

    const img = new Image();
    img.src = imageUrl;
    await new Promise((resolve) => {
      img.onload = resolve;
      img.onerror = resolve;
    });

    return {
      url: imageUrl,
      width: img.naturalWidth || 1,
      height: img.naturalHeight || 1,
      thumbnailUrl: imageUrl,
      publicId: undefined,
      id: undefined,
    };
  }

  async createNode(fileKey: string, request: CreateNodeRequest): Promise<CreateWeavyConnectedNodeResponse> {
    const { data } = await figmaAxios.post<CreateWeavyConnectedNodeResponse>(
      `/v1/files/${fileKey}/weavy/nodes`,
      request,
    );
    return data;
  }

  async updateNode(
    fileKey: string,
    nodeId: string,
    request: UpdateNodeRequest,
  ): Promise<CreateWeavyConnectedNodeResponse> {
    const { data } = await figmaAxios.put<CreateWeavyConnectedNodeResponse>(
      `/v1/files/${fileKey}/weavy/nodes/${nodeId}`,
      request,
    );
    return data;
  }

  async getNodes(fileKey: string, nodeId: string): Promise<GetNodesResponse> {
    const { data } = await figmaAxios.get<GetNodesResponse>(`/v1/files/${fileKey}/nodes/${nodeId}`);
    return data;
  }

  async createImageLayer(
    fileKey: string,
    nodeId: string,
    inputId: string,
    imageDataUrl: string,
    width: number,
    height: number,
  ): Promise<CreateWeavyConnectedNodeResponse> {
    const response = await this.createNode(fileKey, {
      parent_id: nodeId,
      node_type: 'FRAME',
      name: `Figma Weave Input: ${inputId}`,
      weavy_input_node_id: inputId,
      width,
      height,
      fills: [
        {
          url: imageDataUrl,
          type: 'IMAGE',
          blendMode: 'NORMAL',
          scaleMode: 'FILL',
        },
      ],
    });
    return response;
  }

  async createTextLayer(
    fileKey: string,
    nodeId: string,
    inputId: string,
    text: string,
  ): Promise<CreateWeavyConnectedNodeResponse> {
    const response = await this.createNode(fileKey, {
      parent_id: nodeId,
      node_type: 'TEXT',
      name: `Figma Weave Input: ${inputId}`,
      weavy_input_node_id: inputId,
      text: {
        content: text,
      },
    });
    return response;
  }

  async connectNode(
    fileKey: string,
    nodeId: string,
    weavyRecipeId: string,
    compositorNodeId: string,
  ): Promise<ConnectNodeResponse> {
    const body: ConnectNodeRequest = {
      weavyRecipeId,
      compositorNodeId,
      // force: true,
    };

    const { data } = await figmaAxios.post<ConnectNodeResponse>(
      `/v1/files/${fileKey}/weavy/nodes/${nodeId}/connect`,
      body,
    );
    return data;
  }
}

let figmaApiClient: FigmaApiClient | null = null;

export function getOrCreateFigmaApiClient(): FigmaApiClient {
  if (!figmaApiClient) {
    figmaApiClient = new FigmaApiClient();
  }
  return figmaApiClient;
}

export function clearFigmaApiClient() {
  figmaApiClient = null;
}
